import { useEffect, useState } from 'react'
import { isSupportedLanguage, resolveLanguage, type SupportedLanguage } from '../i18n/languagePreference'

export const PRESS_RELEASE_LANGUAGE_STORAGE_KEY = 'finance-lab.press-release.language'

const PRESS_RELEASE_LANGUAGE_CHANGE_EVENT = 'press-release-language-change'

export function getPressReleasePreferredLanguage(): SupportedLanguage {
    return resolveLanguage(getStoredPressReleaseLanguage())
}

function getStoredPressReleaseLanguage(): SupportedLanguage | null {
    try {
        const value = localStorage.getItem(PRESS_RELEASE_LANGUAGE_STORAGE_KEY)
        return isSupportedLanguage(value) ? value : null
    } catch {
        return null
    }
}

export function setPressReleasePreferredLanguage(language: SupportedLanguage): void {
    localStorage.setItem(PRESS_RELEASE_LANGUAGE_STORAGE_KEY, language)
    window.dispatchEvent(new CustomEvent(PRESS_RELEASE_LANGUAGE_CHANGE_EVENT, { detail: language }))
}

export function usePressReleasePreferredLanguage(): [SupportedLanguage, (language: SupportedLanguage) => void] {
    const [language, setLanguage] = useState(() => getPressReleasePreferredLanguage())

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<SupportedLanguage>).detail
            setLanguage(resolveLanguage(detail))
        }

        window.addEventListener(PRESS_RELEASE_LANGUAGE_CHANGE_EVENT, handler)
        return () => window.removeEventListener(PRESS_RELEASE_LANGUAGE_CHANGE_EVENT, handler)
    }, [])

    const setPreferredLanguage = (nextLanguage: SupportedLanguage) => {
        setPressReleasePreferredLanguage(nextLanguage)
        setLanguage(nextLanguage)
    }

    return [language, setPreferredLanguage]
}
